import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Cascader,
  Checkbox,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Mentions,
  Radio,
  Row,
  Select,
  TimePicker,
  Typography,
  Space,
  Modal,
  Upload,
  Tabs,
  message,
} from 'antd';
import { apiClient, apiErrorMessage, getApiBaseUrl, getDataTypeName } from '../../../utils';
import { Field, FieldGroup, FieldPicklistValue, Grid, GridRow } from '../../../models';
import moment from 'moment';
import { DeleteOutlined, LinkOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { FilterSelector, LocalImage } from '../../../elements';
import { FieldFormItem } from '../../grids/field-form-item';
import { FieldsPage } from './fields-page';
import { GridDetailsForm } from './grid-details-form';

export function EditFieldPage(props: {
  gridId?: string;
  fieldGroups?: FieldGroup[];
  fields?: Field[];
  scheme?: any[];
  level?: string[];
  field?: Field;
  onSaved: () => void;
}) {
  const { Option } = Select;

  const [form] = Form.useForm();
  const gridsRef = useRef<Grid[]>();

  const [loading, setLoading] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState<string>();
  const [isIdentifier, setIsIdentifier] = useState<boolean>();
  const [isRevision, setIsRevision] = useState<boolean>();
  const [numericFieldOptions, setNumericFieldOptions] = useState<Field[]>();
  const [isEdit, setIsEdit] = useState(false);
  const [minValueType, setMinValueType] = useState('val');
  const [maxValueType, setMaxValueType] = useState('val');
  const [foreignKeyFieldOptions, setForeignKeyFieldOptions] = useState<any[]>();
  const [linkableFields, setLinkableFields] = useState<Field[]>();
  const [selectedForeignKeyGridId, setSelectedForeignKeyGridId] = useState<string>();
  const [selectedForeignKeyFieldId, setSelectedForeignKeyFieldId] = useState<string>();
  const [lookupTargetOptions, setLookupTargetOptions] = useState<any[]>();
  const [lookupValueOptions, setLookupValueOptions] = useState<any[]>();
  const [selectedLookupGridId, setSelectedLookupGridId] = useState<string>();
  const [selectedLookupFieldId, setSelectedLookupFieldId] = useState<string>();
  const [rules, setRules] = useState<any[]>();
  const [parentOptions, setParentOptions] = useState<any[]>();
  const [addDescriptionLinkModalOpen, setAddDescriptionLinkModalOpen] = useState(false);
  const [existingPicklistValues, setExistingPicklistValues] = useState<FieldPicklistValue[]>();
  const [picklistValueImages, setPicklistValueImages] = useState<{ key: number; file: any }[]>();
  const [picklistImagesToDelete, setPicklisImagesToDelete] = useState<string[]>([]);
  const [isRegexErrorInputVisible, setIsRegexErrorInputVisible] = useState(false);
  const [subGridSettingsModalOpen, setSubGridSettingsModalOpen] = useState<{ isOpen: boolean }>();
  const [grid, setGrid] = useState<Grid>();
  const [unitOfMeasureEnabled, setUnitOfMeasureEnabled] = useState(false);
  const [autoApproveValueTasksEnabled, setAutoApproveValueTasksEnabled] = useState(false);
  const [autoApproveValueTasksType, setAutoApproveValueTasksType] = useState<'all' | 'filter'>('all');

  const baseApiUrl = getApiBaseUrl() + '/api/';

  const integerPattern = /^([0-9]*)$/;
  const signedIntegerPattern = /^-?([0-9]*)$/;
  const signedDecimalPattern = /^-?(([0-9]*)|(([0-9]*)\.([0-9]*)))$/;

  useEffect(() => {
    if (!props.scheme?.length || !props.fieldGroups?.length) {
      setParentOptions(undefined);
      return;
    }

    const addGroup = (schemeItem: any, valuePrefix: string, labelPrefix: string, options: any[]) => {
      const group = props.fieldGroups?.find(g => g.id === schemeItem.id);
      if (!group) {
        return;
      }
      const value = (valuePrefix + (valuePrefix ? '###' : '') + group.id).trim();
      const label = (labelPrefix + (labelPrefix ? ' > ' : '') + group.name).trim();
      const option: any = { value, label };
      options.push(option);

      if (schemeItem.children?.length) {
        for (const item of schemeItem.children) {
          if (item.itemType === 'fieldGroup') {
            addGroup(item, value, label, newOptions);
          }
        }
      }
    };

    const newOptions: any[] = [];
    for (const item of props.scheme) {
      if (item.itemType === 'fieldGroup') {
        addGroup(item, '', '', newOptions);
      }
    }

    setParentOptions(newOptions);
  }, [props.fieldGroups, props.scheme]);

  const onIsIdentifierChange = (e: any) => {
    setIsIdentifier(e.target.checked);
    if (!e.target.checked) {
      setIsRevision(false);
    }
  };

  const onIsRevisionChange = (e: any) => {
    setIsRevision(e.target.checked);
  };

  const onDataTypeChange = (value: string) => {
    setSelectedDataType(value);
    if (
      (value === 'FOREIGNKEY' || value === 'MULTIFOREIGNKEY' || value === 'LOOKUP' || value === 'STRING' || value === 'RULEBASED') &&
      !gridsRef.current
    ) {
      loadGrids();
    }

    form.setFieldsValue({ minValue: null, maxValue: null });
    setSelectedForeignKeyGridId(undefined);
    setSelectedForeignKeyFieldId(undefined);
    setSelectedLookupGridId(undefined);
    setSelectedLookupFieldId(undefined);

    if (value === 'PICKLIST' || value === 'MULTIPICKLIST') {
      form.setFieldsValue({ picklistValues: [{ val: '' }] });
    }
  };

  const loadLinkableFields = useCallback(
    (foreignKeyGridId: string) => {
      apiClient
        .get(`fields?gridId=${props.gridId}&foreignKeyGridId=${foreignKeyGridId}&foreignKeyLinkable=true&dataType=FOREIGNKEY`)
        .then(response => {
          const responseFields = response.data as Field[];
          setLinkableFields(responseFields);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        });
    },
    [props.gridId],
  );

  function openAddFieldModal() {
    loadSubGrid();
  }

  function closeSubGridSettingsdModal() {
    setSubGridSettingsModalOpen({ isOpen: false });
  }

  const loadSubGrid = useCallback(
    (loading?: (loading: boolean) => void) => {
      apiClient
        .get(`grids/${props?.field?.subGridId}`)
        .then((response: any) => {
          setGrid(response.data);
          if (loading) loading(false);
          setSubGridSettingsModalOpen({ isOpen: true });
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        });
    },
    [props.field],
  );

  const onSubGridChanged = (grid?: Grid) => {
    setGrid(grid);
  };

  const loadLookupValueOptions = useCallback((gridId: string) => {
    if (!gridsRef.current?.length) {
      return;
    }

    const grid = gridsRef.current.find(g => g.id === gridId);
    if (!grid) {
      return;
    }

    const lkValueOptions = grid.fields.map(f => {
      return { id: f.id, name: grid.name + ' / ' + f.name };
    });

    setLookupValueOptions(lkValueOptions);
  }, []);

  const loadGrids = useCallback(() => {
    const setOptions = (grids: Grid[]) => {
      const fkOptions = grids
        ?.filter(g => g.id !== props.gridId)
        ?.map(g => {
          return {
            value: g.id,
            label: g.name,
            children: g.fields?.map(f => {
              return {
                value: f.id,
                label: f.name,
              };
            }),
          };
        });
      setForeignKeyFieldOptions(fkOptions);

      const lkOptions = grids
        ?.filter(g => g.id !== props.gridId)
        ?.map(g => {
          return {
            value: g.id,
            label: g.name,
            children: g.fields
              ?.filter(f => f.isUnique)
              ?.map(f => {
                return {
                  value: f.id,
                  label: f.name,
                };
              }),
          };
        })
        .filter(o => o.children?.length);
      setLookupTargetOptions(lkOptions);

      if (props.field?.dataType === 'FOREIGNKEY' || props.field?.dataType === 'MULTIFOREIGNKEY') {
        const foreignKeyFieldId = props.field?.foreignKeyFieldId;
        for (let grid of grids) {
          for (let field of grid.fields) {
            if (field.id === foreignKeyFieldId) {
              setSelectedForeignKeyGridId(grid.id);
              setSelectedForeignKeyFieldId(field.id);
              form.setFieldsValue({ foreignKeyFieldId: [grid.id, field.id] });
              break;
            }
          }
        }

        if (props.field?.foreignKeyGridId) {
          loadLinkableFields(props.field?.foreignKeyGridId);
        }
      }

      let lkTargetGridId = null;
      if (props.field?.dataType === 'LOOKUP') {
        const lookupTargetFieldId = props.field?.lookupTargetFieldId;
        for (let grid of grids) {
          for (let field of grid.fields) {
            if (field.id === lookupTargetFieldId) {
              setSelectedLookupGridId(grid.id);
              lkTargetGridId = grid.id;
              setSelectedLookupFieldId(field.id);
              form.setFieldsValue({ lookupTargetFieldId: [grid.id, field.id] });
              break;
            }
          }
        }

        if (props.field?.lookupValueFieldId && lkTargetGridId) {
          loadLookupValueOptions(lkTargetGridId);
        }
      }
    };

    if (gridsRef.current?.length) {
      setOptions(gridsRef.current);
    } else {
      apiClient
        .get('grids')
        .then(response => {
          const responseGrids = response.data as Grid[];
          gridsRef.current = responseGrids;
          setOptions(responseGrids);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        });
    }
  }, [
    form,
    loadLinkableFields,
    loadLookupValueOptions,
    props.field?.dataType,
    props.field?.foreignKeyFieldId,
    props.field?.foreignKeyGridId,
    props.field?.lookupTargetFieldId,
    props.field?.lookupValueFieldId,
    props.gridId,
  ]);

  useEffect(() => {
    if (props.field) {
      setIsEdit(true);
      setSelectedDataType(props.field?.dataType.toString());
      setIsIdentifier(props.field?.isIdentifier);
      setIsRevision(props.field?.isRevision);
      setIsRegexErrorInputVisible((props.field?.regex?.length || 0) > 0);

      setNumericFieldOptions(props.fields?.filter(f => f.id !== props.field?.id && (f.dataType === 'INTEGER' || f.dataType === 'DOUBLE')));

      const values = { ...props.field } as any;
      if (!values.defaultValue) {
        delete values.defaultValue;
      }
      if (props.field.dataType === 'INTEGER' || props.field.dataType === 'DOUBLE') {
        if (!!props.field.minValueField) {
          values.minValueType = 'ref';
          values.minValueFieldId = props.field.minValueField.id;
          setMinValueType('ref');
        } else {
          values.minValueType = 'val';
          setMinValueType('val');
        }
        if (!!props.field.maxValueField) {
          values.maxValueType = 'ref';
          values.maxValueFieldId = props.field.maxValueField.id;
          setMaxValueType('ref');
        } else {
          values.maxValueType = 'val';
          setMaxValueType('val');
        }
      } else if (props.field.dataType === 'DATE' || props.field.dataType === 'DATETIME') {
        if (props.field.minValue) values.minValue = moment(props.field.minValue);
        if (props.field.maxValue) values.maxValue = moment(props.field.maxValue);
      } else if (props.field.dataType === 'TIME') {
        if (props.field.minValue) values.minValue = moment(props.field.minValue, 'HH:mm');
        if (props.field.maxValue) values.maxValue = moment(props.field.maxValue, 'HH:mm');
      } else if (
        props.field.dataType === 'FOREIGNKEY' ||
        props.field.dataType === 'MULTIFOREIGNKEY' ||
        props.field.dataType === 'LOOKUP' ||
        props.field.dataType === 'STRING'
      ) {
        loadGrids();
        delete values.foreignKeyFieldId;
        delete values.lookupTargetFieldId;
      } else if (props.field.dataType === 'RULEBASED') {
        loadGrids();
        if ((props.field as any).dynamicRule2) {
          const parsedDynamicRule2 =
            typeof (props.field as any).dynamicRule2 === 'string'
              ? JSON.parse((props.field as any).dynamicRule2)
              : (props.field as any).dynamicRule2;
          values.dynamicRule2 = parsedDynamicRule2?.map((r: any) => ({
            ...r,
            filterGroup: typeof r?.filterGroup === 'string' ? JSON.parse(r.filterGroup) : r?.filterGroup,
            template: r?.template ?? r?.thenValue ?? r?.then?.val,
          }));
        }
        form.setFieldsValue(values);
      } else if (props.field.dataType === 'PICKLIST' || props.field.dataType === 'MULTIPICKLIST') {
        setExistingPicklistValues(props.field.picklistValues);
      }
      if (props.field.rule) {
        values.rule = props.field.rule.map(r => {
          const ruleValue = {
            fieldId: r.if[0].fieldId,
            ifOp: r.if[0].val == null ? (r.if[0].op === '==' ? 'null' : '!null') : r.if[0].op,
            ifValue: r.if[0].val === true ? '###true###' : r.if[0].val === false ? '###false###' : r.if[0].val,
            thenOp: r.then.val == null ? (r.then.op === '==' ? 'null' : '!null') : r.then.op,
            thenValue: r.then.val === true ? '###true###' : r.then.val === false ? '###false###' : r.then.val,
            errorMessage: r.errorMessage,
          };
          if (r.if[0].val == null) {
            delete ruleValue.ifValue;
          }
          if (r.then.val == null) {
            delete ruleValue.thenValue;
          }
          return ruleValue;
        });
      }
      if (values.dynamicPostReplace) {
        values.dynamicPostReplace = JSON.parse(values.dynamicPostReplace);
      }

      values.level = props.level?.join('###');

      // Set auto approve type based on filter group presence
      if (props.field.autoApproveValueTasks) {
        values.autoApproveValueTasksType = props.field.autoApproveValueTasksFilterGroup ? 'filter' : 'all';
      }

      form.setFieldsValue(values);
      if (props.field.unitOfMeasureEnabled) {
        setUnitOfMeasureEnabled(true);
      }
      if (props.field.autoApproveValueTasks) {
        setAutoApproveValueTasksEnabled(true);
        if (props.field.autoApproveValueTasksFilterGroup) {
          setAutoApproveValueTasksType('filter');
        } else {
          setAutoApproveValueTasksType('all');
        }
      }
    } else {
      const initialValues = { level: props.level?.join('###') };
      form.setFieldsValue(initialValues);
    }
  }, [props.field, props.fields, form, loadGrids, props.level]);

  function onForeignKeyFieldChange(value: any) {
    if (value.length < 2) {
      setSelectedForeignKeyGridId(undefined);
      setSelectedForeignKeyFieldId(undefined);
    }
    setSelectedForeignKeyGridId(value[0]);
    setSelectedForeignKeyFieldId(value[1]);
    loadLinkableFields(value[0]);
  }

  function onLookupTargetChange(value: any) {
    if (value.length < 2) {
      setSelectedLookupGridId(undefined);
      setSelectedLookupFieldId(undefined);
    }
    setSelectedLookupGridId(value[0]);
    setSelectedLookupFieldId(value[1]);
    loadLookupValueOptions(value[0]);
  }

  const onDynamicFieldSelect = (option: any, prefix: string) => {
    const value = option.value;
    const prefixedValue = prefix + value;
    let fieldValue = form.getFieldValue('dynamicTemplate') as string;
    fieldValue = fieldValue.replace(prefixedValue, value);
    form.setFieldsValue({ dynamicTemplate: fieldValue });
  };

  const onRuleChange = () => {
    setRules(form.getFieldsValue()?.rule);
  };

  const onMinValueTypeChange = (option: string) => {
    setMinValueType(option);
  };

  const onMaxValueTypeChange = (option: string) => {
    setMaxValueType(option);
  };

  const showAddDescriptionLinkModal = () => {
    setAddDescriptionLinkModalOpen(true);
  };

  const closeAddDescriptionLinkModal = () => {
    setAddDescriptionLinkModalOpen(false);
  };

  const onAddDescriptionLinkFinish = (values: any) => {
    const body = {
      ...values,
    };
    const description = form.getFieldValue('description');
    const url = body.descriptionLinkUrl;
    const title = body.descriptionLinkTitle;
    const linkText = `[${title}](${url})`;
    form.setFieldsValue({ description: description + ' ' + linkText });
    closeAddDescriptionLinkModal();
  };

  const onPicklistValueRemove = (index: number) => {
    if (!existingPicklistValues?.length) {
      return;
    }
    const picklistValueId = existingPicklistValues[index]?.id;
    if (picklistValueId) {
      const newExistingPicklistValues = existingPicklistValues?.filter(pv => pv.id !== picklistValueId);
      setExistingPicklistValues(newExistingPicklistValues);
    }
  };

  const onPicklistValueImageChange = (key: number, info: any) => {
    const newPicklistValueImages: { key: number; file: any }[] = picklistValueImages?.length
      ? [...picklistValueImages.filter(v => v.key !== key)]
      : [];
    newPicklistValueImages.push({ key, file: info.file });
    setPicklistValueImages(newPicklistValueImages);
  };

  const onPicklistValueImageRemove = (key: number) => {
    const newPicklistValueImages = picklistValueImages
      ?.filter(v => v.key !== key)
      .map(v => {
        if (v.key > key) {
          v.key--;
        }
        return v;
      });
    setPicklistValueImages(newPicklistValueImages);
  };

  const onExistingPicklistValueImageRemove = (picklistValueId: string) => {
    if (picklistImagesToDelete.some(m => m === picklistValueId)) {
      return;
    }
    const newListToDelete = [...picklistImagesToDelete];
    newListToDelete.push(picklistValueId);
    setPicklisImagesToDelete(newListToDelete);
  };

  const dummyUploadRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const onFinish = (values: any) => {
    setLoading(true);

    let action;
    const body = {
      ...values,
    };

    body.gridId = props.gridId;

    if (body.minValueType === 'val') {
      body.minValueFieldId = null;
    }
    if (body.minValueType === 'ref') {
      body.minValue = null;
    }
    if (body.maxValueType === 'val') {
      body.maxValueFieldId = null;
    }
    if (body.maxValueType === 'ref') {
      body.maxValue = null;
    }

    delete body.minValueType;
    delete body.maxValueType;

    if (body.minValue) {
      if (selectedDataType === 'DATE') {
        body.minValue = body.minValue.format('YYYY-MM-DD');
      } else if (selectedDataType === 'DATETIME') {
        body.minValue = body.minValue.format('YYYY-MM-DD HH:mm');
      } else if (selectedDataType === 'TIME') {
        body.minValue = body.minValue.format('HH:mm');
      }
    }

    if (body.maxValue) {
      if (selectedDataType === 'DATE') {
        body.maxValue = body.maxValue.format('YYYY-MM-DD');
      } else if (selectedDataType === 'DATETIME') {
        body.maxValue = body.maxValue.format('YYYY-MM-DD HH:mm');
      } else if (selectedDataType === 'TIME') {
        body.maxValue = body.maxValue.format('HH:mm');
      }
    }

    if (selectedDataType === 'FOREIGNKEY' || selectedDataType === 'MULTIFOREIGNKEY') {
      body.foreignKeyGridId = selectedForeignKeyGridId;
      body.foreignKeyFieldId = selectedForeignKeyFieldId;
    }

    if (selectedDataType === 'LOOKUP') {
      body.lookupTargetGridId = selectedLookupGridId;
      body.lookupTargetFieldId = selectedLookupFieldId;
      body.priority = 1;
    }

    if ((selectedDataType === 'STRING' || selectedDataType === 'TEXT') && body.isRichTextEditor) {
      if (body.defaultValue === '<p><br></p>') {
        body.defaultValue = undefined;
      }
    }

    if (selectedDataType === 'DYNAMIC') {
      body.priority = 1;
    }

    if (selectedDataType === 'RULEBASED') {
      body.priority = 1;

      if (body.dynamicRule2) {
        body.dynamicRule2 = body.dynamicRule2
          .filter((r: any) => !!r)
          .map((r: any) => ({
            filterGroup:
              r?.filterGroup && r.filterGroup.filterIds?.length
                ? typeof r.filterGroup === 'string'
                  ? r.filterGroup
                  : JSON.stringify(r.filterGroup)
                : undefined,
            template: r.template,
          }))
          .filter((r: any) => r.filterGroup && r.template);
      }
    }

    if (selectedDataType === 'PICKLIST' || selectedDataType === 'MULTIPICKLIST') {
      if (picklistImagesToDelete?.length) {
        body.picklistImagesToDelete = JSON.stringify(picklistImagesToDelete);
      }
    }

    if (body.rule) {
      const rules: any[] = values?.rule;
      const ruleStrs = rules?.map(r => JSON.stringify(r));

      for (const ruleStr of ruleStrs) {
        if (ruleStrs.filter(r => r === ruleStr)?.length > 1) {
          return;
        }
      }

      body.rule = body.rule.map((r: any) => {
        return {
          if: [
            {
              fieldId: r.fieldId,
              op: r.ifOp === 'null' ? '==' : r.ifOp === '!null' ? '!=' : r.ifOp,
              val:
                r.ifOp === 'null' || r.ifOp === '!null'
                  ? null
                  : r.ifValue === '###true###'
                  ? true
                  : r.ifValue === '###false###'
                  ? false
                  : r.ifValue,
            },
          ],
          then: {
            op: r.thenOp === 'null' ? '==' : r.thenOp === '!null' ? '!=' : r.thenOp,
            val:
              r.thenOp === 'null' || r.thenOp === '!null'
                ? null
                : r.thenValue === '###true###'
                ? true
                : r.thenValue === '###false###'
                ? false
                : r.thenValue,
          },
          errorMessage: r.errorMessage,
        };
      });
    }

    if (body.level) {
      body.level = body.level.split('###');
    }

    var formData = new FormData();
    picklistValueImages?.forEach(img => {
      formData.append('files', img.file.originFileObj);
    });

    for (let key of Object.keys(body)) {
      if (key !== 'dragger' && body[key]) {
        if (key !== 'picklistValues') {
          formData.append(key, typeof body[key] === 'object' ? JSON.stringify(body[key]) : body[key]);
        } else {
          const picklistValues = body[key];
          for (let i = 0; i < picklistValues.length; i++) {
            picklistValues[i].order = i;
            const image = picklistValueImages?.find(vi => vi.key === i);
            if (!image) {
              continue;
            }
            picklistValues[i].image = image.file.name;
          }
          formData.append(key, JSON.stringify(body[key]));
        }
      }
    }

    if (props.field) {
      action = apiClient.put(`fields/${props.field?.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      action = apiClient.post('fields', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }

    action
      .then(() => {
        message.success('Değişiklikler kaydedildi.');
        props.onSaved();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showRegexErrorMessageInput = (el: any) => {
    if (!el?.target?.value || el.target.value === '') {
      setIsRegexErrorInputVisible(false);
    } else {
      setIsRegexErrorInputVisible(true);
    }
  };

  return (
    <>
      <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} form={form} onFinish={onFinish}>
        <Form.Item
          label="İsim"
          name="name"
          rules={[
            { required: true, message: 'Zorunlu alan' },
            {
              pattern: /^(?![Mm][Dd][Mm])[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
              message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir; MDM ile başlayamaz',
            },
            { max: 100, message: 'En fazla 100 karakter' },
          ]}
        >
          <Input />
        </Form.Item>

        {isEdit && (
          <Form.Item label="Kod">
            <span className="ant-form-text">{props.field?.code}</span>
          </Form.Item>
        )}

        <Form.Item label="Gösterilen isim" name="displayName" rules={[{ max: 100, message: 'En fazla 100 karakter' }]}>
          <Input />
        </Form.Item>

        <Form.Item
          label="Açıklama"
          name="description"
          rules={[
            { required: true, message: 'Zorunlu alan' },
            { max: 4000, message: 'En fazla 4000 karakter' },
          ]}
          style={{ marginBottom: '2px' }}
        >
          <Input.TextArea />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <Button size="small" onClick={showAddDescriptionLinkModal}>
            <LinkOutlined />
          </Button>
        </div>

        <Form.Item label="Veri elemanı grubu" name="level">
          <Select allowClear>
            {parentOptions?.map(o => (
              <Select.Option value={o.value} key={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedDataType !== 'SUBGRID' && (
          <>
            <Form.Item label="Öne çıkarılmış" name="isHighlighted" valuePropName="checked">
              <Checkbox />
            </Form.Item>

            <Form.Item label="Yeni kayıt ekranında gösterme" name="hideOnNewRecord" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          </>
        )}
        {selectedDataType === 'SUBGRID' && (
          <>
            <Form.Item label="Tablo ekranında bağlantı göster" name="showSubGridLinkOnTable" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Kart detay ekranında bağlantı göster" name="showSubGridLinkOnCardDetail" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          </>
        )}

        <Form.Item label="Veri tipi" name="dataType" rules={[{ required: true, message: 'Zorunlu alan' }]}>
          <Select style={{ width: '100%' }} onChange={onDataTypeChange} disabled={isEdit}>
            <Option value="INTEGER">{getDataTypeName('INTEGER')}</Option>
            <Option value="DOUBLE">{getDataTypeName('DOUBLE')}</Option>
            <Option value="STRING">{getDataTypeName('STRING')}</Option>
            <Option value="TEXT">{getDataTypeName('TEXT')}</Option>
            <Option value="PICKLIST">{getDataTypeName('PICKLIST')}</Option>
            <Option value="MULTIPICKLIST">{getDataTypeName('MULTIPICKLIST')}</Option>
            <Option value="BOOLEAN">{getDataTypeName('BOOLEAN')}</Option>
            <Option value="DATE">{getDataTypeName('DATE')}</Option>
            <Option value="URL">{getDataTypeName('URL')}</Option>
            <Option value="DYNAMIC">{getDataTypeName('DYNAMIC')}</Option>
            <Option value="RULEBASED">{getDataTypeName('RULEBASED')}</Option>
            <Option value="FOREIGNKEY">{getDataTypeName('FOREIGNKEY')}</Option>
            <Option value="MULTIFOREIGNKEY">{getDataTypeName('MULTIFOREIGNKEY')}</Option>
            <Option value="LOOKUP">{getDataTypeName('LOOKUP')}</Option>
            <Option value="AUTOINC">{getDataTypeName('AUTOINC')}</Option>
            <Option value="SUBGRID">{getDataTypeName('SUBGRID')}</Option>
            <Option value="FILE_UPLOAD">{getDataTypeName('FILE_UPLOAD')}</Option>
          </Select>
        </Form.Item>

        {selectedDataType && (
          <>
            {(selectedDataType === 'STRING' || selectedDataType === 'TEXT') && (
              <>
                <Form.Item label="Zengin metin editörü" name="isRichTextEditor" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </>
            )}
            {props.field && props.field.dataType !== 'FILE_UPLOAD' && (
              <FieldFormItem
                form={form}
                gridId={props.field?.gridId}
                field={props.field}
                label="Varsayılan değer"
                name="defaultValue"
                hideTooltip={true}
                notRequired={true}
              />
            )}
            {selectedDataType !== 'DYNAMIC' && selectedDataType !== 'RULEBASED' && selectedDataType !== 'LOOKUP' && (
              <>
                {selectedDataType !== 'FILE_UPLOAD' && selectedDataType !== 'SUBGRID' && (
                  <Form.Item label="Belirteç veri elemanı" name="isIdentifier" valuePropName="checked">
                    <Checkbox onChange={onIsIdentifierChange} />
                  </Form.Item>
                )}
                {isIdentifier && (
                  <>
                    <Form.Item label="Revizyon veri elemanı" name="isRevision" valuePropName="checked">
                      <Checkbox onChange={onIsRevisionChange} />
                    </Form.Item>

                    {isRevision && (
                      <>
                        {selectedDataType === 'INTEGER' && (
                          <Form.Item label="Revizyon maskesi" name="revisionMask">
                            <Input />
                          </Form.Item>
                        )}
                        <Form.Item
                          label="Revizyon sıralaması"
                          name="revisionDirection"
                          rules={[{ required: true, message: 'Zorunlu alan' }]}
                        >
                          <Select>
                            <Option value="DESC">Azalan (En büyük son revizyon)</Option>
                            <Option value="ASC">Artan (En küçük son revizyon)</Option>
                          </Select>
                        </Form.Item>
                      </>
                    )}
                  </>
                )}
                {selectedDataType === 'AUTOINC' && (
                  <Form.Item label="Sayaç maskesi" name="autoIncMask">
                    <Input />
                  </Form.Item>
                )}

                <Form.Item label="Önem seviyesi" name="priority" rules={[{ required: true, message: 'Zorunlu alan' }]}>
                  <Radio.Group>
                    <Radio.Button value={1}>!</Radio.Button>
                    <Radio.Button value={2}>!!</Radio.Button>
                    <Radio.Button value={3}>!!!</Radio.Button>
                  </Radio.Group>
                </Form.Item>

                {!isIdentifier && selectedDataType !== 'AUTOINC' && selectedDataType !== 'SUBGRID' && (
                  <>
                    <Form.Item label="Görevleri otomatik onayla" name="autoApproveValueTasks" valuePropName="checked">
                      <Checkbox
                        onChange={(e: any) => {
                          setAutoApproveValueTasksEnabled(e.target.checked);
                          if (e.target.checked) {
                            const currentType = form.getFieldValue('autoApproveValueTasksType') || 'all';
                            form.setFieldsValue({ autoApproveValueTasksType: currentType });
                            setAutoApproveValueTasksType(currentType);
                          }
                        }}
                      />
                    </Form.Item>
                    {autoApproveValueTasksEnabled && (
                      <Form.Item label="Onay türü" name="autoApproveValueTasksType" labelCol={{ offset: 8, span: 3 }} labelAlign="left">
                        <Radio.Group onChange={e => setAutoApproveValueTasksType(e.target.value)}>
                          <Radio.Button value="all">Tümü</Radio.Button>
                          <Radio.Button value="filter">Filtre</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                    )}
                    {autoApproveValueTasksEnabled && autoApproveValueTasksType === 'filter' && (
                      <Form.Item
                        label="Onay filtresi"
                        name="autoApproveValueTasksFilterGroup"
                        labelCol={{ offset: 8, span: 3 }}
                        labelAlign="left"
                      >
                        <FilterSelector dataSetId={props.gridId} value={props.field?.autoApproveValueTasksFilterGroup} />
                      </Form.Item>
                    )}
                    <Form.Item label="Görevleri ilk aktarımda otomatik onayla" name="autoApproveValueTasksOnCreate" valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                    <Form.Item label="Boş değer için görev oluştur" name="createsEmptyValueTask" valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                  </>
                )}

                {!isIdentifier && selectedDataType !== 'DYANMIC' && selectedDataType !== 'RULEBASED' && (
                  <>
                    <Form.Item label="Kopyalama işlemine dahil et" name="includeInCopy" valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                    <Form.Item label="Önceki revizyon değerini kopyala" name="copyPreviousRevisionValue" valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                  </>
                )}

                {selectedDataType !== 'AUTOINC' && selectedDataType !== 'DYANMIC' && selectedDataType !== 'RULEBASED' && (
                  <Form.Item label="Kuralları atla" name="ignoresValidation" valuePropName="checked" initialValue={false}>
                    <Checkbox />
                  </Form.Item>
                )}

                <Form.Item label="Zorunlu alan" name="isRequired" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </>
            )}

            {selectedDataType !== 'PICKLIST' &&
              selectedDataType !== 'MULTIPICKLIST' &&
              selectedDataType !== 'BOOLEAN' &&
              selectedDataType !== 'FOREIGNKEY' &&
              selectedDataType !== 'MULTIFOREIGNKEY' &&
              selectedDataType !== 'LOOKUP' &&
              selectedDataType !== 'DYNAMIC' &&
              selectedDataType !== 'RULEBASED' &&
              selectedDataType !== 'AUTOINC' &&
              selectedDataType !== 'SUBGRID' &&
              selectedDataType !== 'FILE_UPLOAD' && (
                <Form.Item label="Tekil" name="isUnique" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              )}

            {(selectedDataType === 'STRING' || selectedDataType === 'TEXT' || selectedDataType === 'URL') && (
              <>
                <Form.Item label="En az" name="minValue" rules={[{ pattern: integerPattern, message: 'Sadece tam sayı girilebilir' }]}>
                  <Input />
                </Form.Item>

                <Form.Item label="En çok" name="maxValue" rules={[{ pattern: integerPattern, message: 'Sadece tam sayı girilebilir' }]}>
                  <Input />
                </Form.Item>
              </>
            )}

            {(selectedDataType === 'INTEGER' || selectedDataType === 'DOUBLE' || selectedDataType === 'DECIMAL') && (
              <>
                <Form.Item label="Birim seçilebilsin" name="unitOfMeasureEnabled" valuePropName="checked">
                  <Checkbox onChange={(e: any) => setUnitOfMeasureEnabled(e.target.checked)} />
                </Form.Item>
                {unitOfMeasureEnabled && (
                  <Form.Item label="Birim" name="unitsOfMeasure">
                    <Select mode={'tags'} />
                  </Form.Item>
                )}
              </>
            )}

            {selectedDataType === 'INTEGER' && (
              <>
                <Form.Item label="En az">
                  <Input.Group compact>
                    <Form.Item name="minValueType" noStyle>
                      <Select style={{ width: 'calc(20% - 0.5rem)', marginRight: '0.5rem' }} onChange={onMinValueTypeChange}>
                        <Option value="val">Değer</Option>
                        <Option value="ref">Referans</Option>
                      </Select>
                    </Form.Item>
                    {minValueType === 'val' ? (
                      <Form.Item
                        name="minValue"
                        noStyle
                        rules={[{ pattern: signedIntegerPattern, message: 'Sadece tam sayı girilebilir' }]}
                      >
                        <Input style={{ width: '80%' }} />
                      </Form.Item>
                    ) : (
                      <Form.Item name="minValueFieldId" noStyle>
                        <Select style={{ width: '80%' }}>
                          {numericFieldOptions?.map(o => (
                            <Option key={o.id} value={o.id}>
                              {o.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Input.Group>
                </Form.Item>

                <Form.Item label="En çok">
                  <Input.Group compact>
                    <Form.Item name="maxValueType" noStyle>
                      <Select style={{ width: 'calc(20% - 0.5rem)', marginRight: '0.5rem' }} onChange={onMaxValueTypeChange}>
                        <Option value="val">Değer</Option>
                        <Option value="ref">Referans</Option>
                      </Select>
                    </Form.Item>
                    {maxValueType === 'val' ? (
                      <Form.Item
                        name="maxValue"
                        noStyle
                        rules={[{ pattern: signedIntegerPattern, message: 'Sadece tam sayı girilebilir' }]}
                      >
                        <Input style={{ width: '80%' }} />
                      </Form.Item>
                    ) : (
                      <Form.Item name="maxValueFieldId" noStyle>
                        <Select style={{ width: '80%' }}>
                          {numericFieldOptions?.map(o => (
                            <Option key={o.id} value={o.id}>
                              {o.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Input.Group>
                </Form.Item>
              </>
            )}

            {(selectedDataType === 'MULTIFOREIGNKEY' ||
              selectedDataType === 'FOREIGNKEY' ||
              selectedDataType === 'MULTIPICKLIST' ||
              selectedDataType === 'PICKLIST') && (
              <>
                <Form.Item
                  label="Şablon"
                  name="isTemplate"
                  valuePropName="checked"
                  extra="Şablon içeren kolonlarda doldurulmasını istediğiniz yerleri sırasıyla $1, $2 gibi işaretlemeniz gerekmektedir."
                >
                  <Checkbox />
                </Form.Item>
              </>
            )}

            {(selectedDataType === 'DOUBLE' || selectedDataType === 'DECIMAL') && (
              <>
                <Form.Item label="En az">
                  <Input.Group compact>
                    <Form.Item name="minValueType" noStyle>
                      <Select style={{ width: '20%', marginRight: '0.5rem' }} onChange={onMinValueTypeChange}>
                        <Option value="val">Değer</Option>
                        <Option value="ref">Referans</Option>
                      </Select>
                    </Form.Item>
                    {minValueType === 'val' ? (
                      <Form.Item name="minValue" noStyle rules={[{ pattern: signedDecimalPattern, message: 'Sadece sayı girilebilir' }]}>
                        <Input style={{ width: '80%', marginRight: '0.5rem' }} />
                      </Form.Item>
                    ) : (
                      <Form.Item name="minValueFieldId" noStyle>
                        <Select style={{ width: '80%', marginRight: '0.5rem' }}>
                          {numericFieldOptions?.map(o => (
                            <Option key={o.id} value={o.id}>
                              {o.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Input.Group>
                </Form.Item>

                <Form.Item label="En çok">
                  <Input.Group compact>
                    <Form.Item name="maxValueType" noStyle>
                      <Select style={{ width: '20%' }} onChange={onMaxValueTypeChange}>
                        <Option value="val">Değer</Option>
                        <Option value="ref">Referans</Option>
                      </Select>
                    </Form.Item>
                    {maxValueType === 'val' ? (
                      <Form.Item name="maxValue" noStyle rules={[{ pattern: signedDecimalPattern, message: 'Sadece sayı girilebilir' }]}>
                        <Input style={{ width: '80%' }} />
                      </Form.Item>
                    ) : (
                      <Form.Item name="maxValueFieldId" noStyle>
                        <Select style={{ width: '80%' }}>
                          {numericFieldOptions?.map(o => (
                            <Option key={o.id} value={o.id}>
                              {o.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Input.Group>
                </Form.Item>
              </>
            )}

            {selectedDataType === 'DATE' && (
              <>
                <Form.Item label="En az" name="minValue">
                  <DatePicker />
                </Form.Item>

                <Form.Item label="En çok" name="maxValue">
                  <DatePicker />
                </Form.Item>
              </>
            )}

            {selectedDataType === 'DATETIME' && (
              <>
                <Form.Item label="En az" name="minValue">
                  <DatePicker showTime showSecond={false} />
                </Form.Item>

                <Form.Item label="En çok" name="maxValue">
                  <DatePicker showTime showSecond={false} />
                </Form.Item>
              </>
            )}

            {selectedDataType === 'TIME' && (
              <>
                <Form.Item label="En az" name="minValue">
                  <TimePicker />
                </Form.Item>

                <Form.Item label="En çok" name="maxValue">
                  <TimePicker showSecond={false} />
                </Form.Item>
              </>
            )}

            {(selectedDataType === 'STRING' || selectedDataType === 'TEXT') && (
              <>
                <Form.Item label="RegEx" name="regex">
                  <Input onChange={showRegexErrorMessageInput} />
                </Form.Item>
                {isRegexErrorInputVisible && (
                  <Form.Item label="RegEx hata mesajı" name="regexErrorMessage">
                    <Input />
                  </Form.Item>
                )}
              </>
            )}
          </>
        )}

        {selectedDataType === 'PICKLIST' && (
          <>
            <Form.Item label="İş Süreci" name="isWorkFlow" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          </>
        )}

        {(selectedDataType === 'PICKLIST' || selectedDataType === 'MULTIPICKLIST') && (
          <>
            <Form.List name="picklistValues">
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, index) => (
                    <Form.Item
                      labelCol={{ span: index === 0 ? 8 : 0 }}
                      wrapperCol={{ span: index === 0 ? 16 : 24, offset: index === 0 ? 0 : 8 }}
                      label={index === 0 ? 'Seçenekler' : ''}
                      required={false}
                      key={field.key}
                    >
                      <Row style={{ alignItems: 'center' }}>
                        <Col span={15}>
                          <Form.Item
                            {...field}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                message: 'Zorunlu alan',
                              },
                            ]}
                            name={[field.name, 'val']}
                            noStyle
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={6} style={{ paddingLeft: '1rem' }}>
                          {existingPicklistValues &&
                          (existingPicklistValues?.length || 0) > index &&
                          existingPicklistValues[index].image &&
                          !picklistImagesToDelete.some(m => m === existingPicklistValues[index].id) ? (
                            <div style={{ display: 'flex' }}>
                              <LocalImage
                                src={`${baseApiUrl}fields/picklist-image?name=${existingPicklistValues[index].image}`}
                                alt={existingPicklistValues[index].val}
                                style={{ width: '4rem' }}
                              />
                              <DeleteOutlined
                                style={{ alignSelf: 'start', cursor: 'pointer' }}
                                onClick={() => onExistingPicklistValueImageRemove(props.field!.picklistValues[index].id)}
                              />
                            </div>
                          ) : (
                            <Upload
                              name="files"
                              customRequest={dummyUploadRequest}
                              onChange={(info: any) => {
                                onPicklistValueImageChange(field.name, info);
                              }}
                            >
                              <Button icon={<UploadOutlined />}>Resim ekle</Button>
                            </Upload>
                          )}
                        </Col>
                        <Col span={2} style={{ textAlign: 'right' }}>
                          <MinusCircleOutlined
                            style={{ marginLeft: '0.5rem' }}
                            onClick={() => {
                              remove(field.name);
                              onPicklistValueRemove(index);
                              onPicklistValueImageRemove(field.name);
                            }}
                          />
                        </Col>
                      </Row>
                    </Form.Item>
                  ))}
                  <Form.Item wrapperCol={{ offset: 8 }}>
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                      Seçenek ekle
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </Form.Item>
                </>
              )}
            </Form.List>

            {((picklistValueImages?.length || 0) > 0 ||
              props.field?.picklistValues.some(pv => !picklistImagesToDelete.some(d => d === pv.id) && pv.image)) && (
              <Form.Item label="Resim büyüklüğü" name="picklistImageWidth">
                <InputNumber />
              </Form.Item>
            )}
          </>
        )}

        {(selectedDataType === 'MULTIPICKLIST' || selectedDataType === 'MULTIFOREIGNKEY') && (
          <>
            <Form.Item label="Ayraç" name="arrayValueSeparator" rules={[{ required: true, message: 'Zorunlu alan' }]}>
              <Input />
            </Form.Item>
          </>
        )}

        {selectedDataType === 'DYNAMIC' && (
          <>
            <Form.Item
              label="Şablon"
              name="dynamicTemplate"
              rules={[
                { required: true, message: 'Zorunlu alan' },
                { max: 400, message: 'En fazla 2000 karakter' },
              ]}
            >
              <Mentions rows={3} style={{ width: '100%' }} onSelect={onDynamicFieldSelect}>
                {props.fields
                  ?.filter(f => f.id !== props.field?.id)
                  .map(f => (
                    <Option key={f.id} value={'{{' + f.name + ' || boş}}'}>
                      {f.name}
                    </Option>
                  ))}
              </Mentions>
            </Form.Item>
          </>
        )}

        {selectedDataType === 'RULEBASED' && (
          <div style={{ backgroundColor: '#fafafa', border: '1px solid #d9d9d9', padding: '1rem', marginBottom: '1.5rem' }}>
            <Form.List name="dynamicRule2">
              {(fields, { add, remove }) => {
                if (fields.length === 0) {
                  add();
                }
                return (
                  <>
                    {fields.map((field, index) => (
                      <DynamicRule2FormItem
                        form={form}
                        field={field}
                        index={index}
                        remove={remove}
                        key={field.key}
                        gridFields={props.fields}
                        gridField={props.field}
                        gridId={props.gridId}
                      />
                    ))}
                    <Form.Item wrapperCol={{ offset: 8 }} style={{ marginBottom: 0 }}>
                      <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                        Kural ekle
                      </Button>
                    </Form.Item>
                  </>
                );
              }}
            </Form.List>
          </div>
        )}

        {(selectedDataType === 'DYNAMIC' || selectedDataType === 'RULEBASED') && (
          <Form.Item label="Değer düzenleme">
            <Form.List name="dynamicPostReplace">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(field => (
                    <Space key={field.key} align="baseline">
                      <Form.Item
                        {...field}
                        label="Bul"
                        name={[field.name, 'findValue']}
                        rules={[{ required: true, message: 'Zorunlu alan' }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item {...field} label="Değiştir" name={[field.name, 'newValue']}>
                        <Input />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  ))}

                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Ekle
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        )}

        {(selectedDataType === 'FOREIGNKEY' || selectedDataType === 'MULTIFOREIGNKEY') && (
          <>
            <Form.Item label="Veri seti / veri elemanı" name="foreignKeyFieldId" rules={[{ required: true, message: 'Zorunlu alan' }]}>
              <Cascader
                disabled={!foreignKeyFieldOptions}
                options={foreignKeyFieldOptions}
                onChange={onForeignKeyFieldChange}
                style={{ width: '100%' }}
              />
            </Form.Item>
            {selectedForeignKeyGridId && selectedForeignKeyFieldId && (
              <Form.Item label="Referans filtre grubu" name="foreignKeyFilterGroup">
                <FilterSelector dataSetId={props.gridId} value={props.field?.foreignKeyFilterGroup} />
              </Form.Item>
            )}
            <Form.Item label="Bağlı referans" name="mainForeignKeyFieldId">
              <Select disabled={!linkableFields} allowClear placeholder="Bağlı referans">
                {linkableFields
                  ?.filter(f => f.id !== props.field?.id)
                  .map((linkField: Field) => {
                    return (
                      <Select.Option value={linkField.id} key={linkField.id}>
                        {linkField.name}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </>
        )}

        {selectedDataType === 'LOOKUP' && (
          <>
            <Form.Item label="Aranacak değer" name="lookupSourceFieldId" rules={[{ required: true, message: 'Zorunlu alan' }]}>
              <Select disabled={!props.fields} allowClear placeholder="Veri elemanı">
                {props.fields
                  ?.filter(f => f.id !== props.field?.id)
                  .map((f: Field) => {
                    return (
                      <Select.Option value={f.id} key={f.id}>
                        {f.name}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>

            <Form.Item label="Aranacak veri elemanı" name="lookupTargetFieldId" rules={[{ required: true, message: 'Zorunlu alan' }]}>
              <Cascader
                disabled={!lookupTargetOptions}
                options={lookupTargetOptions}
                onChange={onLookupTargetChange}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item label="Değer veri elemanı" name="lookupValueFieldId" rules={[{ required: true, message: 'Zorunlu alan' }]}>
              <Select disabled={!lookupValueOptions} allowClear placeholder="Veri elemanı">
                {lookupValueOptions?.map((f: Field) => {
                  return (
                    <Select.Option value={f.id} key={f.id}>
                      {f.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </>
        )}

        {selectedDataType === 'SUBGRID' && props.field && (
          <>
            <Form.Item
              label="Denetim kolonlarını göster"
              tooltip="Kaydı oluşturan kullanıcı, kayıt oluşturma ve güncelleme tarihi kolonlarını gösterir."
              name="showAuditColumns"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 8, span: 18 }}>
              <Button htmlType="button" onClick={openAddFieldModal}>
                Alt tablo ayarları
              </Button>
            </Form.Item>
          </>
        )}

        {selectedDataType &&
          selectedDataType !== 'DYNAMIC' &&
          selectedDataType !== 'RULEBASED' &&
          selectedDataType !== 'LOOKUP' &&
          selectedDataType !== 'AUTOINC' &&
          selectedDataType !== 'SUBGRID' &&
          selectedDataType !== 'FILE_UPLOAD' && (
            <Form.Item label="Ön onaylı değerler" name="trustedIntegrationValues">
              <Input />
            </Form.Item>
          )}

        {selectedDataType && selectedDataType === 'FILE_UPLOAD' && (
          <>
            <Form.Item label="Maksimum dosya boyutu (MB)" name="maxFileSize">
              <InputNumber />
            </Form.Item>
            <Form.Item label="Maksimum dosya sayısı" name="maxFileCount">
              <InputNumber />
            </Form.Item>
            <Form.Item label="Dosya uzantıları" name="fileExtensions">
              <Select mode={'tags'}></Select>
            </Form.Item>
          </>
        )}

        {selectedDataType &&
          selectedDataType !== 'DYNAMIC' &&
          selectedDataType !== 'RULEBASED' &&
          selectedDataType !== 'LOOKUP' &&
          selectedDataType !== 'AUTOINC' && (
            <Form.List name="rule">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <RuleFormItem
                      form={form}
                      field={field}
                      index={index}
                      remove={remove}
                      key={field.key}
                      grids={gridsRef.current}
                      gridFields={props.fields}
                      gridField={props.field}
                      gridFieldDataType={selectedDataType}
                      gridFieldForeignKeyGridId={selectedForeignKeyGridId}
                      onRuleChange={onRuleChange}
                      allRules={rules}
                    />
                  ))}
                  <Form.Item wrapperCol={{ offset: 8 }}>
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                      Kural ekle
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          )}

        <Form.Item wrapperCol={{ offset: 8, span: 8 }}>
          <Button type="primary" htmlType="submit" loading={loading}>
            {props.field ? 'Kaydet' : 'Ekle'}
          </Button>
        </Form.Item>
      </Form>
      <Modal
        title="Alt tablo ayarları"
        visible={subGridSettingsModalOpen?.isOpen}
        width="50%"
        footer={null}
        onCancel={closeSubGridSettingsdModal}
        destroyOnClose={true}
      >
        <Tabs>
          <Tabs.TabPane tab="Veri elemanları" key="fields">
            <FieldsPage grid={grid} onSaved={onSubGridChanged} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Detaylar" key="details">
            <GridDetailsForm grid={grid} onSaved={onSubGridChanged} />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
      <Modal
        title="Bağlantı ekle"
        visible={addDescriptionLinkModalOpen}
        width="20%"
        footer={null}
        onCancel={closeAddDescriptionLinkModal}
        destroyOnClose={true}
      >
        <DescriptionLinkForm onFinish={onAddDescriptionLinkFinish} onCancel={closeAddDescriptionLinkModal} />
      </Modal>
    </>
  );
}

function DescriptionLinkForm(props: { onFinish: (values: any) => void; onCancel: () => void }) {
  return (
    <Form layout="vertical" onFinish={props.onFinish}>
      <Form.Item
        label="URL"
        name="descriptionLinkUrl"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 400, message: 'En fazla 400 karakter' },
          { type: 'url', message: 'Geçerli bir URL giriniz' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Metin"
        name="descriptionLinkTitle"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 400, message: 'En fazla 400 karakter' },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={props.onCancel}>İptal</Button>
            <Button type="primary" htmlType="submit">
              Ekle
            </Button>
          </Space>
        </div>
      </Form.Item>
    </Form>
  );
}

function DynamicRule2FormItem(props: {
  form: FormInstance<any>;
  field: FormListFieldData;
  index: number;
  remove: (index: number | number[]) => void;
  gridFields?: Field[];
  gridField?: Field;
  gridId?: string;
}) {
  const { Text } = Typography;
  const { Option } = Select;

  const onDynamicFieldSelect = (option: any, prefix: string) => {
    const value = option.value;
    const prefixedValue = prefix + value;
    const { dynamicRule2 } = props.form.getFieldsValue();
    if (!dynamicRule2 || !dynamicRule2[props.index]) {
      return;
    }
    let fieldValue = (dynamicRule2[props.index]?.template as string) || '';
    fieldValue = fieldValue.replace(prefixedValue, value);
    dynamicRule2[props.index].template = fieldValue;
    props.form?.setFieldsValue({ dynamicRule2 });
  };

  return (
    <Form.Item label={props.index === 0 ? 'Şablonlar' : ''} wrapperCol={{ offset: props.index === 0 ? 0 : 8 }} required={false}>
      <Row align="middle" style={{ marginBottom: '0.5rem' }}>
        <Col span={2}>
          <Text type="secondary">Eğer</Text>
        </Col>
        <Col span={21}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'filterGroup']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <FilterSelector dataSetId={props.gridId} />
          </Form.Item>
        </Col>
        <Col span={1} style={{ textAlign: 'right' }}>
          <MinusCircleOutlined style={{ marginTop: '8px' }} onClick={() => props.remove(props.field.name)} />
        </Col>
      </Row>
      <Row align="middle" style={{ marginBottom: '0.5rem' }}>
        <Col span={2}>
          <Text type="secondary">ise</Text>
        </Col>
        <Col span={21}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'template']}
            style={{ marginBottom: 0 }}
            rules={[
              { required: true, message: 'Zorunlu alan' },
              { max: 400, message: 'En fazla 400 karakter' },
            ]}
          >
            <Mentions placeholder="Şablon" style={{ width: '100%' }} onSelect={onDynamicFieldSelect}>
              {props.gridFields
                ?.filter(f => f.id !== props.gridField?.id)
                .map(f => (
                  <Option key={f.id} value={'{{' + f.name + ' || boş}}'}>
                    {f.name}
                  </Option>
                ))}
            </Mentions>
          </Form.Item>
        </Col>
        <Col span={1} />
      </Row>
    </Form.Item>
  );
}

function RuleFormItem(props: {
  form: FormInstance<any>;
  gridFieldDataType: string;
  field: FormListFieldData;
  index: number;
  remove: (index: number | number[]) => void;
  grids?: Grid[];
  gridFields?: Field[];
  gridField?: Field;
  gridFieldForeignKeyGridId?: string;
  onRuleChange?: () => void;
  allRules?: any[];
}) {
  const { Text } = Typography;

  const [selectedIfField, setSelectedIfField] = useState<Field>();
  const [selectedIfRecordCondition, setSelectedIfRecordCondition] = useState<string>();
  const [ifOps, setIfOps] = useState<any[]>();
  const [selectedIfOp, setSelectedIfOp] = useState<string>();
  const [thenOps, setThenOps] = useState<any[]>();
  const [selectedThenOp, setSelectedThenOp] = useState<string>();
  const [thenRows, setThenRows] = useState<GridRow[]>();
  const [thenRowsLoading, setThenRowsLoading] = useState(false);
  const [ifRows, setIfRows] = useState<GridRow[]>();
  const [ifRowsLoading, setIfRowsLoading] = useState(false);
  const [sameExists, setSameExists] = useState(false);

  const getOperatorsList = useCallback((dataType: string | undefined) => {
    if (!dataType) {
      return undefined;
    }

    const equalOps = [
      'BOOLEAN',
      'DATE',
      'DOUBLE',
      'INTEGER',
      'PICKLIST',
      'MULTIPICKLIST',
      'STRING',
      'TEXT',
      'DATETIME',
      'TIME',
      'DECIMAL',
      'URL',
      'FOREIGNKEY',
      'MULTIFOREIGNKEY',
      'LOOKUP',
      'DYNAMIC',
      'RULEBASED',
      'RECORD_CONDITION',
    ];
    const greaterOps = ['DATE', 'DOUBLE', 'INTEGER', 'DATETIME', 'TIME', 'DECIMAL'];
    const inOps = ['DOUBLE', 'INTEGER', 'STRING', 'DECIMAL', 'URL', 'FOREIGNKEY', 'LOOKUP', 'PICKLIST', 'DYNAMIC', 'RULEBASED'];
    const likeOps = [
      'PICKLIST',
      'MULTIPICKLIST',
      'STRING',
      'TEXT',
      'URL',
      'FOREIGNKEY',
      'MULTIFOREIGNKEY',
      'LOOKUP',
      'DYNAMIC',
      'RULEBASED',
    ];

    const operators = [];

    if (equalOps.some(m => m === dataType)) {
      operators.push({ value: '==', name: 'eşit' }, { value: '!=', name: 'eşit değil' });
    }
    if (greaterOps.some(m => m === dataType)) {
      operators.push(
        { value: '>', name: 'büyük' },
        { value: '>=', name: 'büyük eşit' },
        { value: '<', name: 'küçük' },
        { value: '<=', name: 'küçük eşit' },
      );
    }
    if (inOps.some(m => m === dataType)) {
      operators.push({ value: 'in', name: 'içinde' }, { value: '!in', name: 'içinde değil' });
    }
    if (likeOps.some(m => m === dataType)) {
      operators.push(
        { value: 'startsWith', name: 'başlar' },
        { value: 'endsWith', name: 'biter' },
        {
          value: 'contains',
          name: 'içerir',
        },
      );
    }
    if (dataType !== 'RECORD_CONDITION') {
      operators.push({ value: 'null', name: 'boş' }, { value: '!null', name: 'boş değil' });
    }

    return operators;
  }, []);

  useEffect(() => {
    const { rule } = props.form.getFieldsValue();
    if (rule && rule.length >= props.index + 1 && rule[props.index]) {
      if (!props.gridField) {
        if (rule[props.index].thenValue) {
          delete rule[props.index].thenValue;
        }
        if (rule[props.index].thenOp) {
          delete rule[props.index].thenOp;
          setSelectedThenOp(undefined);
        }
        props.form.setFieldsValue({ rule });
      } else {
        const ifField = props.gridFields?.find(gf => gf.id === rule[props.index].fieldId);
        if (ifField) {
          setSelectedIfField(ifField);
          setIfOps(getOperatorsList(ifField?.dataType));
        } else {
          setSelectedIfRecordCondition(rule[props.index].fieldId);
          setIfOps(getOperatorsList('RECORD_CONDITION'));
        }
        setSelectedIfOp(rule[props.index].ifOp);
        setSelectedThenOp(rule[props.index].thenOp);
      }
    }
    setThenOps(getOperatorsList(props.gridFieldDataType));

    // TODO - Temporarily uses rows end point. Specialized end point for foreign key rows is still in progress.
    const loadForeignKeyRows = () => {
      if (!props.gridFieldForeignKeyGridId) {
        return;
      }
      setThenRows(undefined);
      setThenRowsLoading(true);

      const body = {
        gridId: props.gridFieldForeignKeyGridId,
        limit: 100,
        offset: 0,
        conditions: {
          logic: 'AND',
          items: [
            {
              objectType: 'record',
              objectId: 'MDM_RECORD',
              operator: 'activeStatus',
              value: 'active',
            },
          ],
        },
      };

      apiClient
        .post(`records/records/query`, body)
        .then((response: any) => {
          setThenRows(response.data.records as GridRow[]);
        })
        .catch((reason: any) => {})
        .finally(() => setThenRowsLoading(false));
    };

    if (props.gridFieldDataType === 'FOREIGNKEY' || props.gridFieldDataType === 'MULTIFOREIGNKEY') {
      loadForeignKeyRows();
    }
  }, [
    getOperatorsList,
    props.form,
    props.gridField,
    props.gridFieldDataType,
    props.gridFieldForeignKeyGridId,
    props.gridFields,
    props.index,
  ]);

  useEffect(() => {
    if (!props.allRules) {
      return;
    }
    const rule = props.allRules[props.index];
    setSameExists(props.allRules?.filter(r => JSON.stringify(r) === JSON.stringify(rule))?.length > 1);
  }, [props.allRules, props.index]);

  const onIfFieldChange = (ifFieldId: string) => {
    const { rule } = props.form.getFieldsValue();
    if (rule && rule.length >= props.index + 1) {
      if (rule[props.index]) {
        if (rule[props.index].ifValue) {
          delete rule[props.index].ifValue;
        }
        if (rule[props.index].ifOp) {
          delete rule[props.index].ifOp;
          setSelectedIfOp(undefined);
        }
      }
      props.form.setFieldsValue({ rule });
    }
    const ifField = props.gridFields?.find(gf => gf.id === ifFieldId);
    if (ifField) {
      setSelectedIfRecordCondition(undefined);
      setSelectedIfField(ifField);
      setIfOps(getOperatorsList(ifField?.dataType));
    } else {
      setSelectedIfField(undefined);
      setSelectedIfRecordCondition(ifFieldId);
      setIfOps(getOperatorsList('RECORD_CONDITION'));
    }

    // TODO - Temporarily uses rows endpoint. Specialized endpoint for foreign key rows is still in progress.
    const loadForeignKeyRows = (gridId: string) => {
      if (!gridId) {
        return;
      }
      setIfRows(undefined);
      setIfRowsLoading(true);

      const body = {
        gridId,
        limit: 100,
        offset: 0,
        conditions: {
          logic: 'AND',
          items: [
            {
              objectType: 'record',
              objectId: 'MDM_RECORD',
              operator: 'activeStatus',
              value: 'active',
            },
          ],
        },
      };

      apiClient
        .post(`records/records/query`, body)
        .then((response: any) => {
          setIfRows(response.data.records as GridRow[]);
        })
        .catch((reason: any) => {})
        .finally(() => setIfRowsLoading(false));
    };

    if (ifField?.dataType === 'FOREIGNKEY' || ifField?.dataType === 'MULTIFOREIGNKEY') {
      loadForeignKeyRows(ifField.foreignKeyGridId);
    }

    if (props.onRuleChange) {
      props.onRuleChange();
    }
  };

  const onIfOpChange = (ifOp: string) => {
    const { rule } = props.form.getFieldsValue();
    if (rule && rule.length >= props.index + 1 && rule[props.index] && rule[props.index].ifValue) {
      delete rule[props.index].ifValue;
      props.form.setFieldsValue({ rule });
    }
    setSelectedIfOp(ifOp);

    if (props.onRuleChange) {
      props.onRuleChange();
    }
  };

  const onThenOpChange = (thenOp: string) => {
    const { rule } = props.form.getFieldsValue();
    if (rule && rule.length >= props.index + 1 && rule[props.index] && rule[props.index].thenValue) {
      delete rule[props.index].thenValue;
      props.form.setFieldsValue({ rule });
    }
    setSelectedThenOp(thenOp);

    if (props.onRuleChange) {
      props.onRuleChange();
    }
  };

  const onInputChange = () => {
    if (props.onRuleChange) {
      props.onRuleChange();
    }
  };

  return (
    <Form.Item label={props.index === 0 ? 'Kurallar' : ''} wrapperCol={{ offset: props.index === 0 ? 0 : 8 }} required={false}>
      <Row align="middle" style={{ marginBottom: '0.5rem' }}>
        <Col span={2}>
          <Text type="secondary">Eğer</Text>
        </Col>
        <Col span={21}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'fieldId']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <Select
              placeholder="Veri elemanı / Kayıt"
              onChange={onIfFieldChange}
              showSearch
              filterOption={(input, option) => {
                if (option?.children) {
                  return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ? true : false;
                } else if (option?.label) {
                  return (option.label as string).toLowerCase().indexOf(input.toLowerCase()) >= 0 ? true : false;
                }
                return false;
              }}
              options={[
                {
                  label: 'Veri elemanları',
                  options:
                    (props.gridFields?.length || 0) > 0
                      ? props
                          .gridFields!.filter(gf => gf.id !== props.gridField?.id)
                          .map(gf => {
                            return { label: `${gf.name} (${getDataTypeName(gf.dataType)})`, value: gf.id };
                          })
                      : [],
                },
                {
                  label: 'Kayıt',
                  options: [
                    { label: 'Kural hatası var', value: 'record_errors' },
                    { label: 'Bekleyen görev var', value: 'record_tasks' },
                  ],
                },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={1} style={{ textAlign: 'right' }}>
          <MinusCircleOutlined style={{ marginTop: '8px' }} onClick={() => props.remove(props.field.name)} />
        </Col>
      </Row>
      <Row align="middle" style={{ marginBottom: '0.5rem' }}>
        <Col span={2} />
        <Col span={5} style={{ paddingRight: '0.5rem' }}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'ifOp']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <Select placeholder="İşlem" disabled={!ifOps} onChange={onIfOpChange}>
              {ifOps?.map(o => (
                <Select.Option value={o.value} key={o.value}>
                  {o.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={16}>
          {(selectedIfField?.dataType || selectedIfRecordCondition) && selectedIfOp !== 'null' && selectedIfOp !== '!null' && (
            <>
              {(selectedIfField?.dataType === 'STRING' ||
                selectedIfField?.dataType === 'TEXT' ||
                selectedIfField?.dataType === 'URL' ||
                selectedIfField?.dataType === 'LOOKUP') && (
                <>
                  {(selectedIfOp === '==' ||
                    selectedIfOp === '!=' ||
                    selectedIfOp === 'startsWith' ||
                    selectedIfOp === 'endsWith' ||
                    selectedIfOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'in' || selectedIfOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" onChange={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}

              {selectedIfField?.dataType === 'INTEGER' && (
                <>
                  {(selectedIfOp === '==' ||
                    selectedIfOp === '!=' ||
                    selectedIfOp === '>' ||
                    selectedIfOp === '>=' ||
                    selectedIfOp === '<' ||
                    selectedIfOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        { type: 'integer', message: 'Geçersiz tam sayı' },
                      ]}
                    >
                      <InputNumber placeholder="Değer" style={{ width: '100%' }} disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'in' || selectedIfOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        {
                          validator: async (_, values) => {
                            if (!values || !values.length) {
                              return;
                            }
                            for (let i = 0; i < values.length; i++) {
                              if (!Number.isInteger(parseInt(values[i]))) {
                                return Promise.reject(new Error('Geçersiz tam sayı'));
                              }
                            }
                          },
                        },
                      ]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" onChange={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {(selectedIfField?.dataType === 'DOUBLE' || selectedIfField?.dataType === 'DECIMAL') && (
                <>
                  {(selectedIfOp === '==' ||
                    selectedIfOp === '!=' ||
                    selectedIfOp === '>' ||
                    selectedIfOp === '>=' ||
                    selectedIfOp === '<' ||
                    selectedIfOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <InputNumber placeholder="Değer" style={{ width: '100%' }} disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'in' || selectedIfOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        {
                          validator: async (_, values) => {
                            if (!values || !values.length) {
                              return;
                            }
                            for (let i = 0; i < values.length; i++) {
                              const isNumeric =
                                (typeof values[i] === 'number' || (typeof values[i] === 'string' && values[i].trim() !== '')) &&
                                !isNaN(values[i] as number);
                              if (!isNumeric) {
                                return Promise.reject(new Error('Geçersiz sayı'));
                              }
                            }
                          },
                        },
                      ]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" onChange={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {(selectedIfField?.dataType === 'DATE' || selectedIfField?.dataType === 'DATETIME') && (
                <>
                  {(selectedIfOp === '==' ||
                    selectedIfOp === '!=' ||
                    selectedIfOp === '>' ||
                    selectedIfOp === '>=' ||
                    selectedIfOp === '<' ||
                    selectedIfOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <DatePicker
                        placeholder="Değer"
                        showTime={selectedIfField?.dataType === 'DATETIME'}
                        showSecond={false}
                        style={{ width: '100%' }}
                        disabled={!ifOps}
                        onChange={onInputChange}
                      />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedIfField?.dataType === 'TIME' && (
                <>
                  {(selectedIfOp === '==' ||
                    selectedIfOp === '!=' ||
                    selectedIfOp === '>' ||
                    selectedIfOp === '>=' ||
                    selectedIfOp === '<' ||
                    selectedIfOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <TimePicker placeholder="Değer" style={{ width: '100%' }} disabled={!ifOps} onChange={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {(selectedIfField?.dataType === 'BOOLEAN' || selectedIfRecordCondition) && (
                <>
                  {(selectedIfOp === '==' || selectedIfOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select onChange={onInputChange}>
                        <Select.Option value={'###true###'}>Evet</Select.Option>
                        <Select.Option value={'###false###'}>Hayır</Select.Option>
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {selectedIfField?.dataType === 'PICKLIST' && (
                <>
                  {(selectedIfOp === '==' || selectedIfOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select onChange={onInputChange}>
                        {selectedIfField.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'in' || selectedIfOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" onChange={onInputChange}>
                        {selectedIfField.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {selectedIfField?.dataType === 'MULTIPICKLIST' && (
                <>
                  {(selectedIfOp === '==' || selectedIfOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" onChange={onInputChange}>
                        {selectedIfField.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'startsWith' || selectedIfOp === 'endsWith' || selectedIfOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {selectedIfField?.dataType === 'FOREIGNKEY' && (
                <>
                  {(selectedIfOp === '==' || selectedIfOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select loading={ifRowsLoading} onChange={onInputChange}>
                        {ifRows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'in' || selectedIfOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" loading={ifRowsLoading} onChange={onInputChange}>
                        {ifRows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {selectedIfField?.dataType === 'MULTIFOREIGNKEY' && (
                <>
                  {(selectedIfOp === '==' || selectedIfOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" loading={ifRowsLoading} onChange={onInputChange}>
                        {ifRows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedIfOp === 'startsWith' || selectedIfOp === 'endsWith' || selectedIfOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'ifValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}
        </Col>
        <Col span={1} />
      </Row>
      <Row align="middle" style={{ marginBottom: '0.5rem' }}>
        <Col span={2}>
          <Text type="secondary">ise</Text>
        </Col>
        <Col span={5} style={{ paddingRight: '0.5rem' }}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'thenOp']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <Select placeholder="İşlem" disabled={!ifOps} onChange={onThenOpChange}>
              {thenOps?.map(o => (
                <Select.Option value={o.value} key={o.value}>
                  {o.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={16}>
          {props.gridFieldDataType && selectedThenOp !== 'null' && selectedThenOp !== '!null' && (
            <>
              {(props.gridFieldDataType === 'STRING' ||
                props.gridFieldDataType === 'TEXT' ||
                props.gridFieldDataType === 'URL' ||
                props.gridFieldDataType === 'LOOKUP') && (
                <>
                  {(selectedThenOp === '==' ||
                    selectedThenOp === '!=' ||
                    selectedThenOp === 'startsWith' ||
                    selectedThenOp === 'endsWith' ||
                    selectedThenOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'in' || selectedThenOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" />
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'INTEGER' && (
                <>
                  {(selectedThenOp === '==' ||
                    selectedThenOp === '!=' ||
                    selectedThenOp === '>' ||
                    selectedThenOp === '>=' ||
                    selectedThenOp === '<' ||
                    selectedThenOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        { type: 'integer', message: 'Geçersiz tam sayı' },
                      ]}
                    >
                      <InputNumber placeholder="Değer" style={{ width: '100%' }} disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'in' || selectedThenOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        {
                          validator: async (_, values) => {
                            if (!values || !values.length) {
                              return;
                            }
                            for (let i = 0; i < values.length; i++) {
                              if (!Number.isInteger(parseInt(values[i]))) {
                                return Promise.reject(new Error('Geçersiz tam sayı'));
                              }
                            }
                          },
                        },
                      ]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" />
                    </Form.Item>
                  )}
                </>
              )}
              {(props.gridFieldDataType === 'DOUBLE' || props.gridFieldDataType === 'DECIMAL') && (
                <>
                  {(selectedThenOp === '==' ||
                    selectedThenOp === '!=' ||
                    selectedThenOp === '>' ||
                    selectedThenOp === '>=' ||
                    selectedThenOp === '<' ||
                    selectedThenOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <InputNumber placeholder="Değer" style={{ width: '100%' }} disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'in' || selectedThenOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Zorunlu alan' },
                        {
                          validator: async (_, values) => {
                            if (!values || !values.length) {
                              return;
                            }
                            for (let i = 0; i < values.length; i++) {
                              const isNumeric =
                                (typeof values[i] === 'number' || (typeof values[i] === 'string' && values[i].trim() !== '')) &&
                                !isNaN(values[i] as number);
                              if (!isNumeric) {
                                return Promise.reject(new Error('Geçersiz sayı'));
                              }
                            }
                          },
                        },
                      ]}
                    >
                      <Select mode="tags" notFoundContent={null} placeholder="Değer" onChange={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {(props.gridFieldDataType === 'DATE' || props.gridFieldDataType === 'DATETIME') && (
                <>
                  {(selectedThenOp === '==' ||
                    selectedThenOp === '!=' ||
                    selectedThenOp === '>' ||
                    selectedThenOp === '>=' ||
                    selectedThenOp === '<' ||
                    selectedThenOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <DatePicker
                        placeholder="Değer"
                        showTime={props.gridFieldDataType === 'DATETIME'}
                        showSecond={false}
                        style={{ width: '100%' }}
                        disabled={!ifOps}
                        onChange={onInputChange}
                      />
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'TIME' && (
                <>
                  {(selectedThenOp === '==' ||
                    selectedThenOp === '!=' ||
                    selectedThenOp === '>' ||
                    selectedThenOp === '>=' ||
                    selectedThenOp === '<' ||
                    selectedThenOp === '<=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <TimePicker placeholder="Değer" style={{ width: '100%' }} disabled={!ifOps} onChange={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'BOOLEAN' && (
                <>
                  {(selectedThenOp === '==' || selectedThenOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select onChange={onInputChange}>
                        <Select.Option value={'###true###'}>Evet</Select.Option>
                        <Select.Option value={'###false###'}>Hayır</Select.Option>
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'PICKLIST' && (
                <>
                  {(selectedThenOp === '==' || selectedThenOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select onChange={onInputChange}>
                        {props.gridField?.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'in' || selectedThenOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" onChange={onInputChange}>
                        {props.gridField?.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'MULTIPICKLIST' && (
                <>
                  {(selectedThenOp === '==' || selectedThenOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" onChange={onInputChange}>
                        {props.gridField?.picklistValues?.map(p => (
                          <Select.Option key={p.id} value={p.id}>
                            {p.val}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'startsWith' || selectedThenOp === 'endsWith' || selectedThenOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'FOREIGNKEY' && (
                <>
                  {(selectedThenOp === '==' || selectedThenOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select loading={thenRowsLoading} onChange={onInputChange}>
                        {thenRows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'in' || selectedThenOp === '!in') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" loading={thenRowsLoading} onChange={onInputChange}>
                        {thenRows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                </>
              )}
              {props.gridFieldDataType === 'MULTIFOREIGNKEY' && (
                <>
                  {(selectedThenOp === '==' || selectedThenOp === '!=') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Select mode="multiple" loading={thenRowsLoading} onChange={onInputChange}>
                        {thenRows?.map(r => {
                          const grid = props.grids?.find(g => g.id === r.gridId);
                          const fieldIds = grid?.fields.filter(m => m.isIdentifier).map(m => m.id);
                          const values: string[] = [];
                          fieldIds?.forEach(id => {
                            const cell = r.cells[id];
                            if (cell.value) {
                              values.push(cell.value.toString());
                            }
                          });
                          if (!values.length) {
                            return null;
                          }
                          return (
                            <Select.Option key={r.id} value={r.id}>
                              {values.join(' - ')}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  )}
                  {(selectedThenOp === 'startsWith' || selectedThenOp === 'endsWith' || selectedThenOp === 'contains') && (
                    <Form.Item
                      {...props.field}
                      name={[props.field.name, 'thenValue']}
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: 'Zorunlu alan' }]}
                    >
                      <Input placeholder="Değer" disabled={!ifOps} onKeyUp={onInputChange} />
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}
        </Col>
        <Col span={1} />
      </Row>
      <Row align="middle" style={{ marginBottom: '0.5rem' }}>
        <Col span={2}>
          <Text type="secondary">Mesaj</Text>
        </Col>
        <Col span={21}>
          <Form.Item
            {...props.field}
            name={[props.field.name, 'errorMessage']}
            style={{ marginBottom: 0 }}
            rules={[{ max: 400, message: 'En fazla 400 karakter' }]}
          >
            <Input placeholder="Hata mesajı" />
          </Form.Item>
        </Col>
      </Row>
      {sameExists && <Text type="danger">Tekrar eden kural</Text>}
    </Form.Item>
  );
}
